// Departure airport picker: type a city, airport name or code; nearby/home airports show first.
import React, { useEffect, useId, useRef, useState } from "react";
import { Plane } from "lucide-react";
import { travelApi, flagUrl } from "@/lib/travelApi";

export function AirportPicker({ value, onChange, options = [], label = "Departure airport" }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(null);
  const seq = useRef(0);
  const box = useRef(null);

  const known = picked && picked.code === value ? picked : options.find((o) => o.code === value);
  const shown = open ? q : known ? known.label + " (" + value + ")" : value || "";

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(() => {
      travelApi.searchAirports(term).then((r) => {
        if (my !== seq.current) return;
        setResults((r.items || []).filter((a) => a.iata_code).map((a) => ({
          code: a.iata_code, label: a.name, city: a.municipality || "", cc: (a.iso_country || "").toLowerCase(),
        })));
        setActive(0);
      }).catch(() => my === seq.current && setResults([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);

  const list = q.trim().length >= 2 ? results : options.map((o) => ({ ...o, city: "", cc: "" }));

  const choose = (a) => {
    setPicked({ code: a.code, label: a.label });
    onChange(a.code);
    setQ("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, Math.max(list.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && open && list[active]) { e.preventDefault(); choose(list[active]); }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div ref={box} className="relative">
      <div className="flex h-11 items-center gap-2 rounded-xl border border-border bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
        <Plane className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls={id + "-list"}
          aria-autocomplete="list"
          aria-label={label}
          value={shown}
          placeholder="City, airport or code"
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
          className="h-full w-full min-w-0 bg-transparent text-sm outline-none"
          data-testid="airport-picker-input"
        />
      </div>
      {open && list.length > 0 && (
        <ul id={id + "-list"} role="listbox" className="absolute z-30 mt-1 max-h-72 w-full min-w-[260px] overflow-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
          {list.map((a, i) => (
            <li
              key={a.code + i}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); choose(a); }}
              onMouseEnter={() => setActive(i)}
              className={"flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm " + (i === active ? "bg-accent text-accent-foreground" : "")}
              data-testid="airport-option"
            >
              {a.cc && flagUrl(a.cc, 40) ? <img src={flagUrl(a.cc, 40)} alt="" className="h-4 w-6 shrink-0 rounded-sm object-cover" /> : <Plane className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{a.label}</span>
                {a.city && <span className="block truncate text-xs text-muted-foreground">{a.city}</span>}
              </span>
              <span className="font-mono-num text-xs font-semibold">{a.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AirportPicker;
